import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NbComponentStatus } from '@nebular/theme';
import { BranchServiceMode } from '@shared/enum/branch-service-mode.enum';
import { DialogData } from '@shared/models/dialog-data.model';
import { EnumModel } from '@shared/models/enum.model';
import { HttpErrorModel } from '@shared/models/http-error.model';
import { SettingsModel } from '@shared/models/settings.model';
import { LanguageService } from '@shared/services/language.service';
import { SettingService } from '@shared/services/setting/setting.service';
import { ToastService } from '@shared/services/toast.service';
import { AuthService } from '@shared/services/auth.service';
import { UserService } from '@shared/services/user/user.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { first } from 'rxjs';

@Component({
  selector: 'app-service-mode-settings-dialog',
  templateUrl: 'service-mode-settings-dialog.component.html',
  styleUrls: ['service-mode-settings-dialog.component.scss'],
  standalone: false,
})
export class ServiceModeSettingsDialogComponent implements OnInit {
  serviceModeOptions: EnumModel[];
  submitted = false;
  form: UntypedFormGroup;
  settings: SettingsModel | null = null;
  httpError: HttpErrorModel | null = null;
  restaurantId: string;
  branchId: string;

  constructor(
    private fb: UntypedFormBuilder,
    private spinnerService: NgxSpinnerService,
    private toastService: ToastService,
    private settingsService: SettingService,
    private authService: AuthService,
    private userService: UserService,
    public language: LanguageService,
    public dialogRef: MatDialogRef<ServiceModeSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.serviceModeOptions = [
      { title: 'Menu only', value: BranchServiceMode.MENU_ONLY, active: false },
      { title: 'Menu and payment', value: BranchServiceMode.MENU_AND_PAYMENT, active: false },
      { title: 'Menu and order', value: BranchServiceMode.MENU_AND_ORDER, active: false },
      { title: 'Menu order and payment', value: BranchServiceMode.MENU_ORDER_AND_PAYMENT, active: false },
    ];
    this.branchId = data.branchId;
    this.restaurantId = data.restaurantId;
    this.form = this.fb.group({
      service_mode: [BranchServiceMode.MENU_ORDER_AND_PAYMENT, [Validators.required]],
    });
  }

  ngOnInit() {
    this.loadSettings();
  }

  showNotification(title: string, message: string, type: NbComponentStatus) {
    this.toastService.showTranslatedToast(type, title, message);
  }

  private loadSettings() {
    this.spinnerService.show();
    this.settingsService
      .getSettingstoSpecificBranch(this.restaurantId, this.branchId)
      .pipe(first())
      .subscribe({
        next: (value) => {
          this.settings = value.data;
          const serviceMode = Number(this.settings?.service_mode ?? BranchServiceMode.MENU_ORDER_AND_PAYMENT);
          this.form.patchValue({ service_mode: serviceMode });
          this.spinnerService.hide();
        },
        error: (error: HttpErrorModel) => {
          this.spinnerService.hide();
          this.httpError = error;
        },
      });
  }

  submit() {
    this.submitted = true;
    if (this.form.invalid) {
      this.showNotification('Service Mode', 'Please select a value', 'info');
      return;
    }
    this.updateServiceMode();
  }

  private updateServiceMode() {
    const service_mode = Number(this.form.get('service_mode')?.value);
    this.spinnerService.show();
    this.settingsService
      .editSettingstoSpecificBranch(this.restaurantId, this.branchId, { service_mode })
      .pipe(first())
      .subscribe({
        next: () => {
          this.spinnerService.hide();
          this.closeDialog();
          this.showNotification('Service Mode', 'Service mode updated successfully', 'success');
          this.userService.getUserdetails().subscribe({
            next: res => {
              this.authService.refreshLoginData(res.data);
              window.location.reload();
            },
          });
        },
        error: (error) => {
          this.spinnerService.hide();
          this.httpError = error;
          this.showNotification(
            error?.message || 'Error',
            error?.errors?.toString() || 'Failed to update service mode',
            'danger',
          );
        },
      });
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
